import { useState, useEffect } from "react";
import axios from "axios";
import { 
  ClipboardText, 
  User, 
  Files, 
  SignIn,
  MagnifyingGlass,
  GraduationCap,
  Trash,
  Eye,
  PencilSimple
} from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { ScrollArea } from "@/components/ui/scroll-area";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ActivityLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${API}/logs`);
      setLogs(response.data);
    } catch (error) {
      console.error("Failed to fetch logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case "UPLOAD": return <Files weight="duotone" className="w-4 h-4 text-cyber-primary" />;
      case "VIEW": return <Eye weight="duotone" className="w-4 h-4 text-slate-400" />;
      case "DELETE": return <Trash weight="duotone" className="w-4 h-4 text-cyber-danger" />;
      case "ANALYZE": return <MagnifyingGlass weight="duotone" className="w-4 h-4 text-cyber-volt" />;
      case "LOGIN": return <SignIn weight="duotone" className="w-4 h-4 text-cyber-success" />;
      case "REGISTER": return <User weight="duotone" className="w-4 h-4 text-cyber-success" />;
      case "EXAM_COMPLETED": return <GraduationCap weight="duotone" className="w-4 h-4 text-cyber-primary" />;
      case "UPDATE_DELTA": return <PencilSimple weight="duotone" className="w-4 h-4 text-cyber-volt" />;
      default: return <ClipboardText weight="duotone" className="w-4 h-4 text-slate-400" />;
    }
  };

  const getActionLabel = (action) => {
    switch (action) {
      case "UPLOAD": return "Upload";
      case "VIEW": return "Consultation";
      case "DELETE": return "Suppression";
      case "ANALYZE": return "Analyse";
      case "LOGIN": return "Connexion";
      case "REGISTER": return "Inscription";
      case "EXAM_COMPLETED": return "Examen";
      case "UPDATE_DELTA": return "Mise à jour Delta";
      case "DELETE_USER": return "Suppression utilisateur";
      case "UPDATE_ROLE": return "Changement de rôle";
      default: return action;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case "UPLOAD": return "bg-cyber-primary/10 text-cyber-primary border-cyber-primary/30";
      case "DELETE": 
      case "DELETE_USER": return "bg-cyber-danger/10 text-cyber-danger border-cyber-danger/30";
      case "ANALYZE": 
      case "UPDATE_DELTA": return "bg-cyber-volt/10 text-cyber-volt border-cyber-volt/30";
      case "LOGIN": 
      case "REGISTER": return "bg-cyber-success/10 text-cyber-success border-cyber-success/30";
      default: return "bg-slate-500/10 text-slate-400 border-slate-500/30";
    }
  };

  const formatTimestamp = (isoString) => {
    const date = new Date(isoString);
    return {
      date: date.toLocaleDateString("fr-FR"),
      time: date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })
    };
  };

  const filteredLogs = filter === "all" 
    ? logs 
    : logs.filter(log => log.action === filter);

  const actionTypes = [...new Set(logs.map(log => log.action))];

  return (
    <div data-testid="activity-logs-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tight mb-2">
            Journaux d'activité
          </h1>
          <p className="text-slate-400">Traçabilité complète des actions</p>
        </div>
      </div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-wrap gap-2 mb-6"
      >
        <button
          onClick={() => setFilter("all")}
          className={`px-3 py-1 text-sm rounded-sm border transition-colors ${
            filter === "all" 
              ? "bg-cyber-primary/10 text-cyber-primary border-cyber-primary/30" 
              : "border-cyber-border text-slate-400 hover:border-cyber-primary/50"
          }`}
          data-testid="filter-all"
        >
          Tout ({logs.length})
        </button>
        {actionTypes.map(action => (
          <button
            key={action}
            onClick={() => setFilter(action)}
            className={`px-3 py-1 text-sm rounded-sm border transition-colors ${
              filter === action 
                ? getActionColor(action)
                : "border-cyber-border text-slate-400 hover:border-cyber-primary/50"
            }`}
            data-testid={`filter-${action.toLowerCase()}`}
          >
            {getActionLabel(action)} ({logs.filter(l => l.action === action).length})
          </button>
        ))}
      </motion.div>

      {/* Logs Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-cyber-primary font-mono">Chargement...</div>
        </div>
      ) : filteredLogs.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-16"
        >
          <ClipboardText weight="duotone" className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">Aucune activité</h3>
          <p className="text-slate-400">Les actions seront enregistrées ici</p>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="cyber-card overflow-hidden"
        >
          <ScrollArea className="h-[600px]">
            <table className="w-full cyber-table">
              <thead className="sticky top-0 bg-cyber-surface z-10">
                <tr>
                  <th className="w-32">Horodatage</th>
                  <th className="w-40">Utilisateur</th>
                  <th className="w-32">Action</th>
                  <th>Détails</th>
                  <th className="w-40">Document</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.map((log, index) => {
                  const { date, time } = formatTimestamp(log.timestamp);
                  return (
                    <motion.tr
                      key={log.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.02 }}
                      data-testid={`log-row-${log.id}`}
                    >
                      <td>
                        <div className="text-white">{date}</div>
                        <div className="text-xs text-slate-500">{time}</div>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-cyber-primary/20 rounded-sm flex items-center justify-center">
                            <span className="text-xs text-cyber-primary font-mono">
                              {log.user_name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <div className="text-sm text-white">{log.user_name}</div>
                            <div className="text-xs text-slate-500">{log.user_email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-sm border ${getActionColor(log.action)}`}>
                          {getActionIcon(log.action)}
                          {getActionLabel(log.action)}
                        </span>
                      </td>
                      <td className="text-slate-300 text-sm">{log.details}</td>
                      <td className="text-slate-400 text-sm truncate max-w-[160px]" title={log.document_name}>
                        {log.document_name || "-"}
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </ScrollArea>
        </motion.div>
      )}
    </div>
  );
}
